import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, RotateCcw, ExternalLink, ImageUp, Trash2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HOMEPAGE_FIELDS, HOMEPAGE_DEFAULTS, IMAGE_SLOTS, siteImage } from "@/lib/homepageContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

function ImageSlot({ slot, label, hint }) {
  const { settings, refreshSettings } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const custom = !!settings?.site_images?.[slot];
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/site-images/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await refreshSettings(); toast.success(`${label} updated`); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await api.delete(`/admin/site-images/${slot}`); await refreshSettings(); toast.success(`${label} reset to default`); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border overflow-hidden" data-testid={`site-image-${slot}`}>
      <img src={siteImage(settings, slot)} alt={label} className="w-full aspect-[16/7] object-cover" data-testid={`site-image-preview-${slot}`} />
      <div className="p-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{label}{custom && <span className="ml-2 text-[10px] text-emerald-700 dark:text-emerald-300 font-mono">custom</span>}</p>
          <p className="text-xs text-muted-foreground">{hint} · JPG/PNG/WebP, max 8 MB</p>
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`site-image-input-${slot}`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={busy} data-testid={`site-image-upload-${slot}`} onClick={() => inputRef.current?.click()}><ImageUp size={14} className="mr-1" /> {busy ? "Working…" : "Replace"}</Button>
        {custom && <Button type="button" size="sm" variant="ghost" className="rounded-full text-red-600" disabled={busy} data-testid={`site-image-reset-${slot}`} onClick={reset}><Trash2 size={14} /></Button>}
      </div>
    </div>
  );
}

// Superadmin editor for every text line on the landing page.
export function AdminHomepage() {
  const { refreshSettings } = useAuth();
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => { setValues(data.homepage || {}); setSaved(data.homepage || {}); }).catch((err) => toast.error(errorMessage(err))); }, []);

  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  const set = (k, v) => setValues((s) => { const n = { ...s, [k]: v }; if (v === HOMEPAGE_DEFAULTS[k]) delete n[k]; return n; });
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v && v.trim()));
      const { data } = await api.put("/admin/settings", { homepage: payload });
      setValues(data.homepage || {}); setSaved(data.homepage || {}); refreshSettings?.();
      toast.success("Homepage text saved");
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  const groups = [...new Set(HOMEPAGE_FIELDS.map((f) => f[3]))];
  const customized = Object.keys(values).length;

  return (
    <div data-testid="admin-homepage" className="space-y-6">
      <div className="rounded-2xl border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <p className="font-semibold flex items-center gap-2"><PenLine size={16} /> Homepage & pricing text</p>
          <p className="text-xs text-muted-foreground" data-testid="homepage-status">{customized} of {HOMEPAGE_FIELDS.length} lines customised · placeholders <code>{"{price}"}</code> and <code>{"{price_or_free}"}</code> are filled automatically</p>
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/" target="_blank" data-testid="homepage-preview-link"><ExternalLink size={14} className="mr-1" /> Preview</Link></Button>
          <Button type="button" variant="outline" size="sm" className="rounded-full" data-testid="homepage-reset-all" onClick={() => setValues({})} disabled={customized === 0}><RotateCcw size={14} className="mr-1" /> Reset all</Button>
          <Button type="button" size="sm" data-testid="homepage-save-button" className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950" disabled={!dirty || saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </div>
      <section className="rounded-2xl border bg-card p-6" data-testid="homepage-images">
        <p className="data-label mb-4">Photos</p>
        <div className="grid sm:grid-cols-2 gap-4">{IMAGE_SLOTS.map(([slot, label, hint]) => <ImageSlot key={slot} slot={slot} label={label} hint={hint} />)}</div>
      </section>
      {groups.map((g) => (
        <section key={g} className="rounded-2xl border bg-card p-6" data-testid={`homepage-group-${g.toLowerCase().replace(/\s+/g, "-")}`}>
          <p className="data-label mb-4">{g}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {HOMEPAGE_FIELDS.filter((f) => f[3] === g).map(([key, label, def, , multiline]) => {
              const v = values[key] ?? def;
              const changed = values[key] !== undefined;
              return (
                <div key={key} className={multiline ? "sm:col-span-2" : ""}>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor={`hp-${key}`} className="text-xs font-medium">{label}{changed && <span className="ml-2 text-[10px] text-emerald-700 dark:text-emerald-300 font-mono">edited</span>}</label>
                    {changed && <button type="button" data-testid={`homepage-reset-${key}`} onClick={() => set(key, def)} className="text-[10px] text-muted-foreground hover:text-foreground">reset</button>}
                  </div>
                  {multiline
                    ? <Textarea id={`hp-${key}`} data-testid={`homepage-field-${key}`} value={v} onChange={(e) => set(key, e.target.value)} rows={3} className="rounded-xl text-sm" />
                    : <Input id={`hp-${key}`} data-testid={`homepage-field-${key}`} value={v} onChange={(e) => set(key, e.target.value)} className="rounded-xl h-9 text-sm" />}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
